(ns pfo.component
  (:require [reagent.core :as reagent :refer [atom]]
            [clojure.string :as str]
            [pfo.collection.component :as collection :refer [close-collection-nav]]
            [pfo.assignment.component :as assignment]
            [pfo.document.component :as document]
            [pfo.task.component :as task]
            [pfo.auth :as auth]
            [pfo.auth.model :as auth-model :refer [perm has-perm]]
            [pfo.collection.model :as collection-model :refer [has-aark-enabled-in-any-family]]
            [pfo.search.component :as search]
            [pfo.common.component :as common]
            [pfo.admin.component :as admin]
            [pfo.im.component :as im]
            [pfo.staging.component :as staging]
            [pfo.api :as api]
            [pfo.tz :as tz]
            [pfo.flash :refer [flash]]
            [pfo.infobox :refer [infobox]]
            [pfo.notify :as notify]
            [pfo.util :as util]
            [pfo.state :as state]
            [pfo.time :as time]
            [pfo.flag :refer [enabled]]
            [pfo.widget.modal :refer [modal modal-menu]]))

(def $ js/$)

(defn menu-toggle []
      ; animates the hamburger menu
      ; @see https://jonsuh.com/hamburgers/
      ; @see client/scss/main.scss : div.headerbar div.hamburger
      (.toggleClass ($ "div.hamburger") "is-active")
      ; @see pfo.widget.modal.modal-menu
      ; @see modal-menu component call within this file
      (swap! state/menu-modal assoc :enabled (not (:enabled @state/menu-modal)))
      ; @see client/scss/main.scss : div.menu, div.menu-on
      (.toggleClass ($ "div.menu") "menu-on"))

(defn clock []
  (reagent/with-let [current-time (atom (time/now))
                     interval-id (js/setInterval (fn [] (reset! current-time (time/now))) 1000)]
    [:div.clock "Current time in this demo instance: " (util/date-format-date-hour-minute (tz/to-browser-time @current-time))]
    (finally (js/clearInterval interval-id))))

(defn error-page []
  [common/header-tab-wrapper
   [:span "Error"]
   [:div.o-media
    [:p "An application error has occurred. Please try again. If the problem persists, please contact support."]
    [:footer.u-margin-top--xs [:button {:on-click #(pfo.routes/go-to-last-waypoint :dashboard)} "Back"]]]])

(defn page [component]
  (reagent/create-class
    {:reagent-render
     (fn [component]
       (let [nav-state (if @state/display-collection-nav :collection (state/nav-state-current-area))]
         [:div.outer-container (when @state/force-mobile-menu {:class :mobile-menu})
          [:div.loading "Loading..."]
          (when (exists? js/TIME_SHIFT) [clock])
          [:div.headerbar
           [:div.hamburger {:class "hamburger--slider" :on-click #(menu-toggle) }
            [:span.hamburger-box
             [:span.hamburger-inner]]]
           [:div.logo
            [:img
             {:src js/HEADER_LOGO_URL
              :alt "iPaladin"
              :height "40"}]
            (when (has-aark-enabled-in-any-family)
              [:span.aark-logo
               [:a {:on-click close-collection-nav
                    :class (when (= :chat nav-state) "active")
                    :href "#/chat"}
                [:img
                 {:src "/static/img/brand/aark.png"
                  :alt "AARK"
                  :height "40"}]]])]
           [search/search-field]
           (when (auth-model/can-access-document-review?) [staging/documents-icon])
           (when (and (not (auth-model/is-user-license-admin))
                      (not (collection-model/chat-disabled-in-every-family))) [im/chat-icon])
           [:div.controls
            [:div.greetings "HI, " (:first_name (api/get-auth-info :user))]
            [task/alert-counter]]]
          [:div {:class "page"}
           [:div.main
            [:div.menu.navigation
             [:nav
              {:role "navigation"}
              [:ul#js-navigation-menu.navigation-menu.show
               {:on-click #( when ( < (.innerWidth ($ js/window)) 1024) (menu-toggle))}
               ;; [:button {:on-click (fn [ev] (api/set-auth-info :auth-state :logged-out))} "Clear"]
               (perm :can_view_dashboard
                     [:li.nav-link
                      [:a.dashboard {:on-click close-collection-nav
                                     :class (when (= :dashboard nav-state) "active")
                                     :href "#/"} "Dashboard"]])
               (perm :can_view_collection_nav
                     [:li.nav-link
                      [:a.groups {:class (when (= :collection nav-state) "active")
                                  :on-click (util/handler #(swap! state/display-collection-nav not))
                                  :href "#"} "Family"]])
               (perm :can_view_document
                     [:li.nav-link
                      [:a.documents {:on-click close-collection-nav
                                     :class (when (= :document nav-state) "active")
                                     :href "#/document/list"} "Documents"]])
               (perm :can_view_compliance
                     [:li.nav-link
                      [:a.compliance {:class (when (= :task-compliance nav-state) "active")
                                      :on-click close-collection-nav
                                      :href "#/task/list?task_type=compliance"} "Compliance"]])
               (perm :can_view_task
                     [:li.nav-link
                      [:a.tasks {:class (when (= :task-simple nav-state) "active")
                                 :on-click close-collection-nav
                                 :href "#/task/list?task_type=simple"} "Tasks"]])
               (perm :can_view_event
                     [:li.nav-link
                      [:a.events {:on-click close-collection-nav
                                  :class (when (= :event nav-state) "active")
                                  :href "#/event/list"} "Events"]])
               (when (some identity (map has-perm [:can_view_document :can_view_task :can_view_compliance :can_view_event]) )
                 [:li.nav-link
                  [:a.categories {:on-click close-collection-nav
                                  :class (when (= :category nav-state) "active")
                                  :href "#/category/list"} "Categories"]])
               (when (has-aark-enabled-in-any-family)
                 [:li.nav-link
                  [:a.chat {:on-click close-collection-nav
                            :class (when (= :chat nav-state) "active")
                            :href "#/chat"} [:span.cs-brand "AARK"]]])
               [:li.nav-link
                [:a.settings {:on-click close-collection-nav
                              :class (when (= :settings nav-state) "active")
                              :href (if (auth-model/is-user-admin) "#/admin/user/list" "#/task/templategroup/list")} "Settings"]]
               (when (enabled "saml")
                 [:li.nav-link
                  [:a.support {:on-click close-collection-nav
                               :href (pfo.routes/path-for :support)
                               :target "_blank"} "Support"]])
               [:li.nav-link
                [:a.logout {:on-click close-collection-nav
                            :href "#/logout"} "Logout"]]]]
             [:div.c-nav-patents "Protected by one or more of the following US Patents: #10,789,572; #11,631,051; #12,314,903."]]

            ^{:key :main} [:div.main-center (if @state/display-collection-nav {:class "u-display--none"}) component]
            (if @state/display-collection-nav ^{:key :nav} [collection/collection-nav2]) ]
           [assignment/collection-tree-modal]
           [:div.document-preview-modal [document/document-preview-modal]]
           [modal-menu state/menu-modal menu-toggle ]
           [document/document-add-modal]
           [task/step-sign-auth-modal]
           [task/compliance-edit-conflicts-modal]
           [task/task-add-modal]
           [common/confirmation-modal]
           [common/action-modal]
           [common/controled-confirmation-modal]
           [admin/template-copy-modal]
           [common/modals]
           [flash]
           [infobox]
           (when (and (not (auth-model/is-user-license-admin))
                      (not (collection-model/chat-disabled-in-every-family))) [im/chat-panel])]]))

     :component-did-mount (fn [_] (notify/connect!))

     :component-will-unmount (fn [_] (notify/disconnect!))

     :component-did-catch (fn [_ e info]
                            (let [;; Info is an JS object, eg.:
                                  ;; #js {:componentStack "\npfo.assignment.component.assignments_add_new_notable@http://localhost:3000/st...
                                  error-location (->
                                                   info
                                                   js->clj
                                                   (get "componentStack")
                                                   ;; str is a safety measure in case of nil or non-string value,
                                                   ;; str/ functions are sensitive to nil
                                                   str
                                                   (str/trim)
                                                   (str/split #"@")
                                                   first)]
                              (api/report-error :sync nil (ex-message e) error-location)))
     :get-derived-state-from-error (fn [err]
                                     (util/set-page error-page)
                                     #js {})}))

(add-watch state/loading-count :loading-state (fn [_ _ olds news]
                                  (if (> news 0)
                                    (.show ($ ".loading"))
                                    (.hide ($ ".loading")))))
